/* spring-book — <spring-booking> custom element.

   A custom Sesami Storefront SDK booking wizard for event / experience PDPs.
   The trigger is the host spring-pdp section's "Book Now" button; this element
   owns the modal: guests -> calendar -> (time) -> confirm -> reserve -> add to cart.

   Tokenless: the SDK authenticates via shopId + product context. No secret here.

   Flow:
     experience : guests -> date -> time -> confirm
     event      : guests -> date -> confirm  (auto-picks the day's single slot;
                  falls back to a time step when a day has more than one slot)

   Party size is chosen in the wizard's own "Select guests" step (no longer the
   PDP's external stepper, which now renders for normal products only). The
   maximum is the service's Sesami `groupAppointment` value, read live via the
   SDK's getConfig() — never hardcoded.

   Solo services (groupAppointment = 1) have no party-size choice: the cap is
   prefetched at page load, so open() routes straight to the calendar and the
   guests step never renders (a neutral holding view covers the rare case
   where the cap is still loading when the modal opens). A party of 1 is
   implicit — the confirm summary omits its Guests row (the cart's booking
   panel does the same, see snippets/cart-item.liquid).

   Cart integration reuses the theme's verified contract (same as spring-pdp.js):
     POST /cart/add.js -> GET /cart.js
     dispatch "cart:changed" { detail: { cartData: { ...cart, items_added } } }
     dispatch "cart:item-added"
     click [data-action='show-cart-drawer']   (fallback: navigate to /cart)

   Critical Sesami gotchas honoured here:
     - window.sesami = sdk  BEFORE any SDK method call (internals use the global).
     - quantity (group size) is a CONSTRUCTION-time option (no setQuantity); the
       booking SDK is therefore built once the guest count is confirmed.
     - getSesamiFields() output is parsed inertly (DOMParser) and re-emitted as
       fresh hidden inputs — never injected into the live DOM.
*/
(function () {
  if (customElements.get('spring-booking')) return;

  class SpringBooking extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.mode = this.dataset.mode === 'event' ? 'event' : 'experience';
      this.shopId = this.dataset.shopId;
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.productTitle = this.dataset.productTitle || '';

      // Refs (captured before the overlay is portalled to <body>).
      this.overlay = this.querySelector('[data-book-overlay]');
      this.modal = this.querySelector('[data-book-modal]');
      this.titleEl = this.querySelector('[data-book-title]');
      // Single header control: the back arrow doubles as "close" on the first step.
      this.backBtn = this.querySelector('[data-book-back]');
      this.footEl = this.querySelector('[data-book-foot]');
      this.nextBtn = this.querySelector('[data-book-next]');
      this.nextLabel = this.querySelector('[data-next-label]');
      // Guests step.
      this.guestsStepper = this.querySelector('[data-guests-stepper]');
      this.guestsValue = this.querySelector('[data-guests-value]');
      this.guestsDec = this.querySelector('[data-guests-step="-1"]');
      this.guestsInc = this.querySelector('[data-guests-step="1"]');
      this.guestsCap = this.querySelector('[data-guests-cap]');
      // Calendar step.
      this.calEl = this.querySelector('[data-cal]');
      this.calGrid = this.querySelector('[data-cal-grid]');
      this.calMonth = this.querySelector('[data-cal-month]');
      this.calYear = this.querySelector('[data-cal-year]');
      this.calToday = this.querySelector('[data-cal-today]');
      this.calPrev = this.querySelector('[data-cal-prev]');
      this.calNext = this.querySelector('[data-cal-next]');
      // Time step (week strip + slots).
      this.weekEl = this.querySelector('[data-week]');
      this.weekGrid = this.querySelector('[data-week-grid]');
      this.weekMonth = this.querySelector('[data-week-month]');
      this.weekYear = this.querySelector('[data-week-year]');
      this.weekToday = this.querySelector('[data-week-today]');
      this.weekPrev = this.querySelector('[data-week-prev]');
      this.weekNext = this.querySelector('[data-week-next]');
      this.slotsEl = this.querySelector('[data-slots]');
      this.slotsStatus = this.querySelector('[data-slots-status]');
      this.slotNote = this.querySelector('[data-slot-note]');
      // Confirm step.
      this.summaryEl = this.querySelector('[data-summary]');
      this.consentBox = this.querySelector('[data-consent]');
      this.loadingEl = this.querySelector('[data-loading]');
      this.errorEl = this.querySelector('[data-error]');
      this.steps = Array.from(this.querySelectorAll('[data-step]'));

      // Host PDP + trigger. (The legacy external stepper, if present on a
      // non-bookable page, seeds the initial guest count for back-compat.)
      this.pdp = this.closest('spring-pdp');
      this.openBtn = this.pdp && this.pdp.querySelector('[data-spring-book-open]');
      this.qtyInput = this.pdp && this.pdp.querySelector('[data-qty-input]');

      this._guests = 1;
      if (this.qtyInput) {
        const seed = parseInt(this.qtyInput.value, 10);
        if (seed && seed > 0) this._guests = seed;
      }
      this._maxGuests = null; // populated from groupAppointment once config loads
      this._configPromise = null;
      this._configSettled = false;

      this.state = { day: null, daySlots: [], slot: null };
      this._slotCache = new Map();
      this._monthOffset = 0;
      this._weekRowIndex = null;
      this._sdkReady = false;

      // Portal the overlay to <body> so position:fixed is never clipped or
      // z-index-trapped by the section's sticky media / fixed action bar.
      if (this.overlay && this.overlay.parentNode !== document.body) {
        document.body.appendChild(this.overlay);
      }

      this.bindEvents();

      // Prefetch the group-size cap now rather than on first open: by the time
      // the visitor clicks Book Now the cap is (almost always) already known,
      // so open() can route solo services straight to the calendar instead of
      // flashing the guests step while the config request runs.
      this.ensureConfig();
    }

    /* ---- guests (internal state, chosen in the wizard) ---- */
    get guests() {
      return this._guests && this._guests > 0 ? this._guests : 1;
    }

    bindEvents() {
      if (this.openBtn) {
        this.openBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      }
      if (this.backBtn) this.backBtn.addEventListener('click', () => this.back());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());
      if (this.calToday) this.calToday.addEventListener('click', () => this.goToday());
      if (this.calPrev) this.calPrev.addEventListener('click', () => this.changeMonth('previous'));
      if (this.calNext) this.calNext.addEventListener('click', () => this.changeMonth('next'));
      if (this.weekToday) this.weekToday.addEventListener('click', () => this.goToday());
      if (this.weekPrev) this.weekPrev.addEventListener('click', () => this.changeWeek('previous'));
      if (this.weekNext) this.weekNext.addEventListener('click', () => this.changeWeek('next'));

      if (this.consentBox) {
        this.consentBox.addEventListener('change', () => {
          if (this.currentStep === 'confirm') this.setNext(this.consentBox.checked);
        });
      }

      if (this.guestsStepper) {
        this.guestsStepper.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-guests-step]');
          if (!btn) return;
          this.changeGuests(parseInt(btn.dataset.guestsStep, 10) || 0);
        });
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', (e) => {
          if (e.target === this.overlay) this.close();
        });
      }
      document.addEventListener('keydown', (e) => {
        if (!this._open) return;
        if (e.key === 'Escape') this.close();
        else if (e.key === 'Tab') this.trapFocus(e);
      });
    }

    /* ---- open / close ---- */
    async open() {
      if (!this.overlay) return;
      // The PDP's variant dropdown may have switched the target variant since
      // the last open — re-read it and drop all variant-scoped state (SDK,
      // slot cache, group-size cap) so the wizard rebuilds for the new variant.
      const dsVariant = this.dataset.variantId;
      if (dsVariant && dsVariant !== this.variantId) {
        this.variantId = dsVariant;
        this._sdkReady = false;
        this._slotCache.clear();
        this._configPromise = null;
        this._configSettled = false;
        this._maxGuests = null;
        this.state = { day: null, daySlots: [], slot: null };
      }
      // First open per page view = the visitor entering the booking flow.
      // Re-opens after a close are not a new start, so fire once.
      if (!this._startTracked) {
        this._startTracked = true;
        this.track('booking_start');
      }
      this._open = true;
      this.overlay.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => this.overlay.classList.add('is-open'));
      if (this.backBtn) this.backBtn.focus();
      this.startFlow();
    }

    close() {
      if (!this.overlay) return;
      this._open = false;
      this.overlay.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      const overlay = this.overlay;
      window.setTimeout(() => {
        if (!this._open) overlay.hidden = true;
      }, 200);
      if (this.openBtn) this.openBtn.focus();
    }

    /* Reset per-open state and land on the right first step. Solo sessions
       (groupAppointment = 1) have nothing to choose, so they open directly on
       the calendar — the guests step never renders (no flash-then-skip) and no
       booking_guests_confirm fires (it wasn't a user action). The cap is
       prefetched in connectedCallback; only when that fetch is still in flight
       does a neutral holding view bridge the gap. Config-fetch failure ->
       guests step, uncapped (fail-open). */
    async startFlow() {
      const run = (this._flowRun = (this._flowRun || 0) + 1);
      this.state.day = null;
      this.state.daySlots = [];
      this.state.slot = null;
      this._weekRowIndex = null;
      // Every booking re-collects explicit consent (the modal persists in the DOM).
      if (this.consentBox) this.consentBox.checked = false;
      this.renderGuests();

      if (this._maxGuests == null && !this._configSettled) {
        this.goToStep('loading');
        this.showLoading(true);
        await this.ensureConfig();
        this.showLoading(false);
        // A close (or close + reopen) while waiting supersedes this run.
        if (run !== this._flowRun || !this._open) return;
      }

      if (this._maxGuests === 1) {
        this._guests = 1;
        this.renderGuests();
        this.goToStep('date');
        if (!this._sdkReady || this._sdkQuantity !== 1) {
          this._sdkReady = false;
          await this.initSDK();
        }
      } else {
        this.goToStep('guests');
      }
    }

    /* True when the product renders no consent checkbox, or it's been ticked. */
    consentGiven() {
      return !this.consentBox || this.consentBox.checked;
    }

    /* ---- guests step ---- */
    renderGuests() {
      if (this.guestsValue) this.guestsValue.textContent = String(this.guests);
      const max = this._maxGuests || Infinity;
      if (this.guestsDec) this.guestsDec.disabled = this.guests <= 1;
      if (this.guestsInc) this.guestsInc.disabled = this.guests >= max;
      // Cap caption — states the service's group-size limit (Sesami
      // groupAppointment, fetched async in ensureConfig) so a disabled "+"
      // never reads as broken. Silent until the cap is known; textContent is
      // only touched on change so the live region doesn't re-announce every
      // +/- click.
      if (this.guestsCap) {
        let msg = '';
        if (this._maxGuests === 1) msg = 'This session is for 1 guest';
        else if (this._maxGuests) msg = 'Up to ' + this._maxGuests + ' guests per session';
        if (this.guestsCap.textContent !== msg) this.guestsCap.textContent = msg;
        this.guestsCap.hidden = !msg;
      }
    }

    changeGuests(delta) {
      if (!delta) return;
      const max = this._maxGuests || Infinity;
      const next = Math.min(max, Math.max(1, this.guests + delta));
      if (next === this._guests) return;
      this._guests = next;
      this.renderGuests();
    }

    /* ---- GTM dataLayer ----
       The wizard funnel is tracked through these events ONLY (no click-id
       triggers — a click id alongside a same-named dataLayer event made GA4
       count every step twice). Params carry what an id can't: guest count,
       chosen date/time. No-op when GTM is absent. */
    track(event, params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(
        Object.assign(
          {
            event: event,
            booking_product: this.productTitle,
            booking_mode: this.mode,
            booking_guests: this.guests,
          },
          params || {}
        )
      );
    }

    // Date/time of the chosen slot as event params. startTime is the SDK's
    // "YYYY-MM-DD HH:mm" string — split raw rather than via Date so the values
    // stay timezone-stable for analytics.
    slotParams() {
      const parts = this.state.slot && String(this.state.slot.startTime || '').split(' ');
      if (!parts || parts.length < 2) return {};
      return { booking_date: parts[0], booking_time: parts[1] };
    }

    async confirmGuests() {
      // Fired on the click (not after SDK init), mirroring the GTM click trigger.
      this.track('booking_guests_confirm');
      // Group size is a CONSTRUCTION-time SDK option, so the booking SDK is built
      // (or rebuilt) here with the confirmed count, then we advance to the calendar.
      if (!this._sdkReady || this._sdkQuantity !== this.guests) {
        this._sdkReady = false;
        await this.initSDK();
      }
      if (!this._sdkReady) return; // init failed; error shown, stay on guests
      this.goToStep('date');
    }

    /* ---- config (group-size cap) ---- */
    // Memoized: the first caller (connectedCallback, at page load) starts the
    // fetch; later callers await the same promise. open() drops the memo on a
    // variant switch so the cap is re-read for the new variant.
    ensureConfig() {
      if (!this._configPromise) this._configPromise = this.fetchConfig();
      return this._configPromise;
    }

    async fetchConfig() {
      const forVariant = this.variantId;
      let cap = null;
      try {
        await this.waitForSDK();
        // getConfig() is product-scoped (independent of quantity); a throwaway
        // quantity:1 instance is enough to read the cap without loading slots.
        const cfgSdk = new window.SesamiSDK({
          shopId: Number(this.shopId),
          productId: Number(this.productId),
          variantId: Number(this.variantId),
          quantity: 1,
        });
        const config = await cfgSdk.getConfig();
        cap = config && config.product && Number(config.product.groupAppointment);
      } catch (err) {
        if (window.console) console.debug('[spring-book] group-size config unavailable:', err);
      }
      // A variant switch mid-flight restarted the fetch — drop the stale result.
      if (forVariant !== this.variantId) return;
      this._configSettled = true;
      if (cap && cap > 0) this._maxGuests = cap;
      this.applyCapClamp();
    }

    applyCapClamp() {
      if (this._maxGuests && this.guests > this._maxGuests) this._guests = this._maxGuests;
      this.renderGuests();
    }

    /* ---- SDK boot ---- */
    async initSDK() {
      this.showLoading(true);
      this.showError('');
      try {
        await this.waitForSDK();
        // reserve() holds capacity for the quantity the SDK was built with, so we
        // build it with the chosen guest count: the reservation holds the right
        // number of seats and availability is filtered by capacity.
        this._sdkQuantity = this.guests;
        this._monthOffset = 0;
        this._slotCache.clear();
        this.sdk = new window.SesamiSDK({
          shopId: Number(this.shopId),
          productId: Number(this.productId),
          variantId: Number(this.variantId),
          quantity: this._sdkQuantity,
          autoLoad: true,
        });
        // MUST precede any SDK call — Sesami internals reference the bare global.
        window.sesami = this.sdk;
        await this.sdk.init();
        this._sdkReady = true;
        this.renderCalendar();
        this.updateMonthNav();
      } catch (err) {
        this.showError('Booking is temporarily unavailable. Please try again later.');
        if (window.console) console.error('[spring-book] SDK init failed:', err);
      } finally {
        this.showLoading(false);
      }
    }

    waitForSDK() {
      return new Promise((resolve, reject) => {
        if (window.SesamiSDK) return resolve();
        let tries = 0;
        const timer = window.setInterval(() => {
          if (window.SesamiSDK) {
            window.clearInterval(timer);
            resolve();
          } else if (++tries > 100) {
            window.clearInterval(timer);
            reject(new Error('Sesami SDK failed to load.'));
          }
        }, 100);
      });
    }

    /* ---- calendar ---- */
    renderCalendar() {
      const data = this.sdk && this.sdk.data;
      if (!data || !this.calGrid) return;
      const rows = data.rows || [];
      const flat = rows.reduce((acc, r) => acc.concat(r), []);

      const firstCur = flat.find((d) => d.isCurrentMonth) || flat[0];
      // day.date is a Date object (per Sesami SDK docs), not a string.
      if (firstCur && firstCur.date instanceof Date) {
        if (this.calMonth) {
          this.calMonth.textContent = firstCur.date.toLocaleDateString(undefined, { month: 'long' });
        }
        if (this.calYear) this.calYear.textContent = String(firstCur.date.getFullYear());
        // Keep the group label in sync with the visible month for screen readers.
        this.calGrid.setAttribute(
          'aria-label',
          'Calendar, ' + firstCur.date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
        );
      }

      this.calGrid.replaceChildren();

      (data.labels || []).forEach((lbl) => {
        const dow = document.createElement('span');
        dow.className = 'spring-book__dow spring-overline';
        const d = lbl instanceof Date ? lbl : new Date(lbl);
        dow.textContent = isNaN(d) ? '' : d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2);
        this.calGrid.appendChild(dow);
      });

      flat.forEach((day) => {
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'spring-book__day';
        cell.textContent = day.date instanceof Date ? String(day.date.getDate()) : '';
        if (day.date instanceof Date) cell.setAttribute('aria-label', this.fmtDayLabel(day.date));
        if (!day.isCurrentMonth) cell.classList.add('is-other');
        if (day.isToday) cell.classList.add('is-today');

        const selectable = day.isCurrentMonth && !day.isPast;
        if (!selectable) {
          cell.disabled = true;
        } else {
          cell.classList.add('is-checking');
          this.getDaySlots(day)
            .then((slots) => {
              cell.classList.remove('is-checking');
              const open = slots.filter((s) => this.slotOk(s));
              if (open.length) {
                cell.classList.add('has-slots');
                cell.setAttribute('aria-pressed', this.sameDay(day, this.state.day) ? 'true' : 'false');
                cell.addEventListener('click', () => this.selectDay(day, open, cell));
              } else {
                cell.disabled = true;
              }
            })
            .catch(() => {
              cell.classList.remove('is-checking');
              cell.disabled = true;
            });
        }
        this.calGrid.appendChild(cell);
      });
    }

    // Full spoken date for day-cell buttons, e.g. "Saturday 14 June 2026" —
    // the visible cell stays a bare day number.
    fmtDayLabel(date) {
      return date.toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }

    slotOk(s) {
      if (!s) return false;
      // The availability flag/field names vary across SDK versions, and the
      // reference doc has already proven unreliable (day.date). Be permissive:
      // reject only slots that are explicitly unavailable, then gate on capacity.
      const status = (s.status || s.state || s.availability || '').toString().toLowerCase();
      if (status && /unavail|booked|full|sold|past|closed|expired|reserved/.test(status)) return false;
      if (s.available === false || s.isAvailable === false || s.bookable === false) return false;
      const remaining =
        s.remainingSlots != null ? s.remainingSlots :
        s.remaining != null ? s.remaining :
        s.spotsLeft != null ? s.spotsLeft :
        s.capacity != null ? s.capacity : null;
      if (remaining == null || isNaN(Number(remaining))) return true;
      return Number(remaining) >= this.guests;
    }

    getDaySlots(day) {
      const key = day.date instanceof Date ? day.date.getTime() : day;
      if (this._slotCache.has(key)) return Promise.resolve(this._slotCache.get(key));
      return Promise.resolve(day.getAvailabilities()).then((slots) => {
        const arr = Array.isArray(slots) ? slots : slots ? [slots] : [];
        this._slotCache.set(key, arr);
        return arr;
      });
    }

    selectDay(day, openSlots, cell) {
      this.state.day = day;
      this.state.daySlots = openSlots;
      this.state.slot = null;

      const prev = this.calGrid.querySelector('.spring-book__day.is-selected');
      if (prev) {
        prev.classList.remove('is-selected');
        prev.setAttribute('aria-pressed', 'false');
      }
      if (cell) {
        cell.classList.add('is-selected');
        cell.setAttribute('aria-pressed', 'true');
      }

      const needTime = this.mode === 'experience' || openSlots.length > 1;
      if (needTime) {
        this._weekRowIndex = this.findRowIndex(day);
        this.renderWeek();
        this.renderSlots(openSlots);
        this.goToStep('time');
      } else {
        this.state.slot = openSlots[0];
        this.renderSummary();
        this.goToStep('confirm');
      }
    }

    async changeMonth(dir) {
      if (!this.sdk) return;
      if (dir === 'previous' && this._monthOffset <= 0) return;
      this.setCalLoading(true);
      this.showError('');
      try {
        if (dir === 'next') {
          await this.sdk.next('month');
          this._monthOffset += 1;
        } else {
          await this.sdk.previous('month');
          this._monthOffset -= 1;
        }
        this.renderCalendar();
      } catch (err) {
        this.showError('Could not load that month. Please try again.');
      } finally {
        this.setCalLoading(false);
        this.updateMonthNav();
      }
    }

    updateMonthNav() {
      if (this.calPrev) this.calPrev.disabled = this._monthOffset <= 0;
    }

    // Calendar-icon shortcut: jump back to the current month (and, on the time
    // step, today's week with its first bookable day reselected).
    goToday() {
      if (!this.sdk) return;
      this.setCalLoading(true);
      this.showError('');
      try {
        this.sdk.setDate(new Date());
        this._monthOffset = 0;
        this.renderCalendar();
        if (this.currentStep === 'time') {
          const rows = this.rows();
          let idx = rows.findIndex((r) => r.some((d) => d.isToday));
          if (idx < 0) idx = 0;
          this._weekRowIndex = idx;
          this.state.day = null;
          this.state.daySlots = [];
          this.state.slot = null;
          this.renderWeek();
          this.autoSelectFirstInWeek(rows[idx]);
        }
      } catch (err) {
        this.showError('Could not jump to today. Please try again.');
      } finally {
        this.setCalLoading(false);
        this.updateMonthNav();
      }
    }

    setCalLoading(on) {
      if (this.calEl) this.calEl.classList.toggle('is-loading', !!on);
    }

    /* ---- week strip (time step) ----
       The strip is the calendar row containing the selected day, letting guests
       nudge to adjacent days without returning to the month view. Week arrows
       move within the loaded month's rows (cross-month jumps use Back -> the
       month calendar -> month arrows). */
    sameDay(a, b) {
      return (
        a && b && a.date instanceof Date && b.date instanceof Date && a.date.getTime() === b.date.getTime()
      );
    }

    rows() {
      return (this.sdk && this.sdk.data && this.sdk.data.rows) || [];
    }

    findRowIndex(day) {
      const rows = this.rows();
      if (!day || !(day.date instanceof Date)) return 0;
      const t = day.date.getTime();
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].some((d) => d.date instanceof Date && d.date.getTime() === t)) return i;
      }
      return 0;
    }

    renderWeek() {
      if (!this.weekGrid) return;
      const rows = this.rows();
      if (this._weekRowIndex == null || !rows[this._weekRowIndex]) {
        this._weekRowIndex = this.findRowIndex(this.state.day);
      }
      const row = rows[this._weekRowIndex] || [];

      const labelDay = this.state.day || row.find((d) => d.isCurrentMonth) || row[0];
      if (labelDay && labelDay.date instanceof Date) {
        if (this.weekMonth) this.weekMonth.textContent = labelDay.date.toLocaleDateString(undefined, { month: 'long' });
        if (this.weekYear) this.weekYear.textContent = String(labelDay.date.getFullYear());
        // Keep the group label in sync with the visible week for screen readers.
        this.weekGrid.setAttribute(
          'aria-label',
          'Week, ' + labelDay.date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
        );
      }

      this.weekGrid.replaceChildren();
      (this.sdk.data.labels || []).forEach((lbl) => {
        const dow = document.createElement('span');
        dow.className = 'spring-book__dow spring-overline';
        const d = lbl instanceof Date ? lbl : new Date(lbl);
        dow.textContent = isNaN(d) ? '' : d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2);
        this.weekGrid.appendChild(dow);
      });

      row.forEach((day) => {
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = 'spring-book__wday';
        cell.textContent = day.date instanceof Date ? String(day.date.getDate()) : '';
        if (day.date instanceof Date) cell.setAttribute('aria-label', this.fmtDayLabel(day.date));
        if (!day.isCurrentMonth) cell.classList.add('is-other');
        const isSel = this.sameDay(day, this.state.day);
        if (isSel) cell.classList.add('is-selected');

        if (day.isPast) {
          cell.disabled = true;
        } else {
          cell.classList.add('is-checking');
          this.getDaySlots(day)
            .then((slots) => {
              cell.classList.remove('is-checking');
              const open = slots.filter((s) => this.slotOk(s));
              if (open.length) {
                cell.classList.add('has-slots');
                cell.setAttribute('aria-pressed', isSel ? 'true' : 'false');
                cell.addEventListener('click', () => this.selectWeekDay(day, open, cell));
              } else if (!isSel) {
                cell.disabled = true;
              }
            })
            .catch(() => {
              cell.classList.remove('is-checking');
              if (!isSel) cell.disabled = true;
            });
        }
        this.weekGrid.appendChild(cell);
      });

      if (this.weekPrev) this.weekPrev.disabled = this._weekRowIndex <= 0;
      if (this.weekNext) this.weekNext.disabled = this._weekRowIndex >= rows.length - 1;
    }

    selectWeekDay(day, openSlots, cell) {
      this.state.day = day;
      this.state.daySlots = openSlots;
      this.state.slot = null;

      const prev = this.weekGrid.querySelector('.spring-book__wday.is-selected');
      if (prev && prev !== cell) {
        prev.classList.remove('is-selected');
        prev.setAttribute('aria-pressed', 'false');
      }
      if (cell) {
        cell.classList.add('is-selected');
        cell.setAttribute('aria-pressed', 'true');
      }

      if (this.weekMonth && day.date instanceof Date) {
        this.weekMonth.textContent = day.date.toLocaleDateString(undefined, { month: 'long' });
        if (this.weekYear) this.weekYear.textContent = String(day.date.getFullYear());
      }

      this.renderSlots(openSlots);
      this.setNext(false);
    }

    changeWeek(dir) {
      const rows = this.rows();
      const idx = (this._weekRowIndex == null ? 0 : this._weekRowIndex) + (dir === 'next' ? 1 : -1);
      if (idx < 0 || idx >= rows.length) return;
      this._weekRowIndex = idx;
      this.state.day = null;
      this.state.daySlots = [];
      this.state.slot = null;
      this.renderWeek();
      this.autoSelectFirstInWeek(rows[idx]);
    }

    async autoSelectFirstInWeek(row) {
      for (const day of row || []) {
        if (day.isPast) continue;
        let open;
        try {
          open = (await this.getDaySlots(day)).filter((s) => this.slotOk(s));
        } catch (e) {
          continue;
        }
        if (open.length) {
          this.state.day = day;
          this.state.daySlots = open;
          this.state.slot = null;
          this.renderWeek();
          this.renderSlots(open);
          this.setNext(false);
          return;
        }
      }
      // No bookable day in this week.
      this.renderSlots([]);
      this.setNext(false);
    }

    /* ---- time slots ---- */
    renderSlots(slots) {
      if (!this.slotsEl) return;
      this.slotsEl.replaceChildren();

      if (!slots.length) {
        // Empty state lives in a persistent role="status" element so screen
        // readers announce it (a node injected into a plain div would be silent).
        this.setSlotsStatus(
          'No times available for ' + this.guests + (this.guests === 1 ? ' guest' : ' guests') + ' on this day.'
        );
        this.updateSlotNote(null);
        this.setNext(false);
        return;
      }
      this.setSlotsStatus('');

      slots.forEach((slot) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'spring-book__slot spring-body';
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = this.fmtTime(slot.startTime);
        btn.addEventListener('click', () => {
          const sel = this.slotsEl.querySelector('.spring-book__slot.is-selected');
          if (sel) {
            sel.classList.remove('is-selected');
            sel.setAttribute('aria-pressed', 'false');
          }
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          this.state.slot = slot;
          this.setNext(true);
        });
        this.slotsEl.appendChild(btn);
      });
      this.updateSlotNote(slots);
      this.setNext(false);
    }

    setSlotsStatus(msg) {
      if (!this.slotsStatus) return;
      this.slotsStatus.textContent = msg || '';
      this.slotsStatus.hidden = !msg;
    }

    updateSlotNote(slots) {
      if (!this.slotNote) return;
      const dur = slots && slots.length ? Number(slots[0].duration) : 0;
      if (dur && dur > 0) {
        this.slotNote.textContent = 'The selected slot will be reserved for ' + dur + ' minutes.';
        this.slotNote.hidden = false;
      } else {
        this.slotNote.hidden = true;
      }
    }

    /* ---- confirm ---- */
    renderSummary() {
      if (!this.summaryEl) return;
      this.summaryEl.replaceChildren();

      const rows = [];
      // Chosen variant first (multi-variant bookables, e.g. duration
      // packages) — read live from the PDP bar's variant mirror so the
      // summary always matches the dropdown. data-variant-label carries the
      // option name ("Duration"); absent on single-variant products.
      const variantLabel = this.dataset.variantLabel;
      const variantEl = this.pdp && this.pdp.querySelector('[data-variant-subtitle]');
      if (variantLabel && variantEl && variantEl.textContent.trim()) {
        rows.push([variantLabel, variantEl.textContent.trim()]);
      }
      // A party of 1 is implicit (and fixed on solo services) — the Guests row
      // only appears for real groups.
      if (this.guests > 1) rows.push(['Guests', String(this.guests)]);
      if (this.state.day && this.state.day.date instanceof Date) {
        rows.push(['Date', this.state.day.date.toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        })]);
      }
      if (this.state.slot) rows.push(['Time', this.fmtTime(this.state.slot.startTime)]);
      const price = this.pdp && this.pdp.querySelector('.spring-pdp__price');
      if (price && price.textContent.trim()) rows.push(['Price', price.textContent.trim()]);

      rows.forEach(([k, v]) => {
        const dt = document.createElement('dt');
        dt.className = 'spring-book__sum-k spring-body';
        dt.textContent = k;
        const dd = document.createElement('dd');
        dd.className = 'spring-book__sum-v spring-body-strong';
        dd.textContent = v;
        this.summaryEl.append(dt, dd);
      });
    }

    /* ---- step machine ---- */
    goToStep(step) {
      this.currentStep = step;
      this.steps.forEach((s) => {
        const active = s.dataset.step === step;
        s.hidden = !active;
        if (active) s.setAttribute('data-active', '');
        else s.removeAttribute('data-active');
      });
      this.showError('');

      const titles = { guests: 'Select guests', date: 'Book a date', time: 'Book a time', confirm: 'Confirm booking' };
      if (this.titleEl) this.titleEl.textContent = titles[step] || '';

      // The back arrow is always present; on the first step (or the pre-config
      // holding view) it dismisses.
      const dismisses = step === 'guests' || step === 'loading';
      if (this.backBtn) this.backBtn.setAttribute('aria-label', dismisses ? 'Close' : 'Back');

      // The Confirm button is restored to view by default; the time step hides it
      // again until a slot is chosen (see setNext).
      if (this.nextBtn) this.nextBtn.hidden = false;
      // The slot-note lives in the footer and is time-step only.
      if (this.slotNote && step !== 'time') this.slotNote.hidden = true;

      // Footer: full-width Confirm on guests/confirm; on the time step it becomes
      // a row with the reservation note (left) and an inline Confirm pill (right)
      // that only appears once a slot is picked.
      const showFoot = step === 'guests' || step === 'time' || step === 'confirm';
      if (this.footEl) {
        this.footEl.hidden = !showFoot;
        this.footEl.classList.toggle('spring-book__foot--inline', step === 'time');
      }
      if (step === 'guests') {
        this.setNextLabel('Confirm');
        this.setNext(true);
      } else if (step === 'time') {
        this.setNextLabel('Confirm');
        this.setNext(!!this.state.slot);
      } else if (step === 'confirm') {
        this.setNextLabel('Confirm & add to cart');
        this.setNext(this.consentGiven());
      }
    }

    next() {
      if (this.currentStep === 'guests') {
        this.confirmGuests();
      } else if (this.currentStep === 'time') {
        if (!this.state.slot) return;
        this.track('booking_time_confirm', this.slotParams());
        this.renderSummary();
        this.goToStep('confirm');
      } else if (this.currentStep === 'confirm') {
        this.submitBooking();
      }
    }

    back() {
      if (this.currentStep === 'guests' || this.currentStep === 'loading') {
        this.close();
      } else if (this.currentStep === 'date') {
        // Solo sessions skipped the guests step, so back from the calendar
        // dismisses rather than landing on a step that never showed.
        if (this._maxGuests === 1) this.close();
        else this.goToStep('guests');
      } else if (this.currentStep === 'time') {
        this.goToStep('date');
      } else if (this.currentStep === 'confirm') {
        const hadTime = this.mode === 'experience' || this.state.daySlots.length > 1;
        this.goToStep(hadTime ? 'time' : 'date');
      }
    }

    /* ---- reserve -> cart ---- */
    async submitBooking() {
      const slot = this.state.slot;
      if (!slot || !this.sdk) return;
      if (!this.consentGiven()) return; // button is disabled, but stay defensive
      this.setNext(false);
      this.showLoading(true);
      this.showError('');
      try {
        // reserve() POSTs to Sesami's /api/v1/reservation and resolves to
        // { id, token, expiresIn, expiresAt }. The `token` (base64 of the
        // reservation id) is what Sesami uses to convert the Shopify order into
        // an appointment — it must ride along as the `_reservation_token`
        // line-item property. The v0.0.3 SDK's getSesamiFields() omits it
        // (it only emits Date/Time/Timezone/Teammember), so we attach it here.
        // Verified against the live API; a null teamMember is accepted and
        // Sesami auto-assigns an "Any Available" resource at conversion.
        const reservation = await this.sdk.reserve(slot);
        const token =
          reservation && (reservation.token || reservation._reservation_token || reservation.reservationToken);

        const form = document.createElement('form');
        this.appendSesamiFields(form, this.sdk.getSesamiFields(slot));
        if (token && !form.querySelector('[name="properties[_reservation_token]"]')) {
          this.appendHidden(form, 'properties[_reservation_token]', token);
        }
        this.appendHidden(form, 'id', this.variantId);
        this.appendHidden(form, 'quantity', String(this.guests));
        if (this.consentBox) {
          // Same line-item property contract as the legacy product-form
          // checkbox, but stamped at click time (Liquid 'now' is render-cached).
          const ts = new Date().toISOString().slice(0, 19).replace('T', ' ');
          this.appendHidden(form, 'properties[_consent_signed]', 'signed-on-' + ts);
        }

        if (!token && window.console) {
          console.warn('[spring-book] no reservation token in', reservation, '— booking may not register in Sesami');
        }

        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const added = await addRes.json();
        if (!addRes.ok) {
          throw new Error(added.description || added.message || 'Could not add this booking to your cart.');
        }

        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const itemsAdded = (added.items || [added]).map((i) => ({
          variant_id: i.variant_id,
          quantity: i.quantity,
        }));

        document.dispatchEvent(
          new CustomEvent('cart:changed', { detail: { cartData: Object.assign({}, cart, { items_added: itemsAdded }) } })
        );
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: added } }));

        // Fired only after reserve + add-to-cart succeed, so "complete" means a
        // booking actually landed in the cart (not just a click on the button).
        this.track('booking_form_complete', this.slotParams());

        this.close();
        const drawerTrigger = document.querySelector("[data-action='show-cart-drawer']");
        if (drawerTrigger) drawerTrigger.click();
        else window.location.href = '/cart';
      } catch (err) {
        this.showError((err && err.message) || 'Could not complete your booking. Please try again.');
        this.setNext(this.consentGiven());
      } finally {
        this.showLoading(false);
      }
    }

    /* getSesamiFields() may return a string, a Node, a NodeList, or an array.
       Parse inertly and re-emit only name/value pairs as fresh hidden inputs —
       never insert vendor-produced markup into the live DOM. */
    appendSesamiFields(form, fields) {
      const found = [];
      const collect = (node) => {
        if (!node) return;
        if (typeof node === 'string') {
          const doc = new DOMParser().parseFromString(node, 'text/html');
          doc.querySelectorAll('input, textarea, select').forEach((el) => found.push(el));
          return;
        }
        if (node.nodeType === 1) {
          if (node.matches && node.matches('input, textarea, select')) found.push(node);
          if (node.querySelectorAll) node.querySelectorAll('input, textarea, select').forEach((el) => found.push(el));
          return;
        }
        if (typeof node.forEach === 'function') {
          node.forEach(collect);
          return;
        }
        if (typeof node.length === 'number') {
          Array.prototype.forEach.call(node, collect);
        }
      };
      collect(fields);
      found.forEach((el) => {
        if (el.name) this.appendHidden(form, el.name, el.value);
      });
    }

    appendHidden(form, name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value == null ? '' : value;
      form.appendChild(input);
    }

    /* ---- ui helpers ---- */
    setNext(on) {
      if (this.nextBtn) this.nextBtn.disabled = !on;
      // On the time step the footer stays visible (it holds the reservation note);
      // only the inline Confirm pill is hidden until a slot is picked.
      if (this.currentStep === 'time' && this.nextBtn) this.nextBtn.hidden = !on;
    }

    setNextLabel(text) {
      if (this.nextLabel) this.nextLabel.textContent = text;
      else if (this.nextBtn) this.nextBtn.textContent = text;
    }

    showLoading(on) {
      if (this.loadingEl) this.loadingEl.hidden = !on;
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
    }

    trapFocus(e) {
      if (!this.modal) return;
      const focusable = Array.prototype.slice
        .call(this.modal.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    fmtTime(startTime) {
      if (!startTime) return '';
      const d = new Date(String(startTime).replace(' ', 'T'));
      if (isNaN(d)) return String(startTime).split(' ')[1] || String(startTime);
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
  }

  customElements.define('spring-booking', SpringBooking);
})();
